// resolveDdlEdit — shared DSE2 pipeline call used by `commitDdlEdit` and
// `previewDdlEdit`.
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.2 — Commit Pipeline + Buffer Sync.
//
// Wraps the (extractDdlStructure → applyDdlEdit → emitSource) flow against a
// `SqlResolverService`. Pure: never mutates Monaco, never mutates the cached
// `ParsedBuffer`. Both round-trip and preview paths funnel here so neither
// parallel-implements byte-range patching (Codebase Pattern → "Commit pipeline").
//
// Error handling: SR-Edit's `EmitSourceError` (codes `overlapping-edits`,
// `edit-out-of-bounds`, `inverted-range`, `edit-inside-comment`) and DSE2's
// `DdlEditError` (codes `engine-does-not-support-op`, `invalid-edit` — the
// concrete realisation of the PRD's "EngineDoesNotSupportOp" contract) are
// the error classes we expect — we let them propagate so the caller surfaces
// a loud failure instead of silently corrupting SQL.

import type { DdlEdit, ModifiedDdlStatement } from '../../../sql-resolver/types';
import type { ApplyDdlEditOptions } from '../../../sql-resolver/ddl/apply/applyDdlEdit';
import type { SqlResolverService } from '../../../sql-resolver/service/resolverService';

export type ResolveDdlEditCtx = {
  readonly connectionId?: string | null;
  readonly database?: string | null;
  readonly applyOptions?: ApplyDdlEditOptions;
  readonly service: SqlResolverService;
  readonly documentId: string;
  readonly statementId: string;
};

export type ResolvedDdlEdit = {
  readonly modified: ModifiedDdlStatement;
  readonly emittedSource: string;
  readonly originalSource: string;
};

export function resolveDdlEdit(
  edit: DdlEdit,
  ctx: ResolveDdlEditCtx,
): ResolvedDdlEdit {
  const { service, documentId, statementId } = ctx;
  const buffer = service.getParsedBuffer(documentId);
  if (!buffer) {
    throw new Error(
      `resolveDdlEdit: no document '${documentId}' is open in the resolver service`,
    );
  }
  const modified = service.applyDdlEdit(documentId, statementId, edit, ctx.applyOptions);
  const emittedSource = service.emitSource(documentId, modified);
  return {
    modified,
    emittedSource,
    originalSource: buffer.parsedDocument.source,
  };
}

/** Sum of replacement bytes across `plannedEdits`. Used for threshold gating. */
export function totalReplacementBytes(modified: ModifiedDdlStatement): number {
  let total = 0;
  for (const e of modified.plannedEdits) {
    // Match emitSource semantics — splices replacements as JS strings; unit
    // is UTF-16 code units (equivalent to ASCII-byte length for the DDL
    // VqeDdlEdit emits today).
    total += e.replacement.length;
  }
  return total;
}
